<template>
  <internal-process
    class="review-status-across-clients-container"
    @go-back="backHandler"
    :process-name="processTitle"
    :with-main-background="withMainBackground"
    :with-footer-background="withFooterBackground"
  >
    <template v-if="isLoading">
      <mds-loader />
    </template>
    <template v-else-if="fundReviewDetails">
      <div class="fund-details-wrapper">
        <div class="fund-name-container">
          <h4 class="field-value">{{fundReviewDetails.displayName}}</h4>
        </div>
        <div class="fund-details-container">
          <div class="fund-details fund-border">
            <span class="field-name">FundID:</span>
            <span class="field-value">{{fundReviewDetails.fundID}}</span>
          </div>
          <div class="fund-details fund-border">
            <span class="field-name">SecID:</span>
            <span class="field-value">{{fundReviewDetails.secId}}</span>
          </div>
          <div class="fund-details">
            <span class="field-name">Ticker:</span>
            <span class="field-value">{{fundReviewDetails.ticker}}</span>
          </div>
        </div>
      </div>
      <mds-section
        class="review-status-share-class-container"
        collapsible
        expanded
        container
        title="Shared Classes"
        :size="7"
        bold
      >
        <mds-table class="review-status-across-clients-table">
          <mds-thead>
            <mds-th style="width: 40%;"> Fund Name </mds-th>
            <mds-th style="width: 15%;"> Ticker </mds-th>
            <mds-th> Fund Status </mds-th>
            <mds-th> Pending </mds-th>
            <mds-th style="width: 20%;"> Current Review Status </mds-th>
          </mds-thead>
          <mds-tbody>
            <template v-for="(row, i) in rows">
              <mds-tr :key="i + 1 + row.ticker" class="share-class-fund-details">
                <mds-td> {{row.displayName}} </mds-td>
                <mds-td> {{row.ticker}} </mds-td>
                <mds-td></mds-td>
                <mds-td></mds-td>
                <mds-td>
                  <div v-if="fiterAllClientReviewStatus(row).length > 0">
                    <ReviewStatusIcons
                      :status="[...fiterAllClientReviewStatus(row)]"
                      :fullLength="true"
                    />
                  </div>
                </mds-td>
              </mds-tr>
              <mds-tr
                v-if="row.FundAndReviewStatusesForClient.length > 0"
                :key="i + 2 + row.ticker"
                type="expandable"
                :expanded="checkIfClientExpanded(row)"
                class="client-expandable"
              >
                <mds-td
                  class="client-label"
                  @click.native="toggleClient(i)"
                  @keyup.enter.native="toggleClient(i)"
                >
                Clients
                </mds-td>
                <mds-td></mds-td>
                <mds-td></mds-td>
                <mds-td></mds-td>
              </mds-tr>
              <mds-tr
                v-if="row.FundAndReviewStatusesForClient.length > 0 && checkIfClientExpanded(row)"
                :key="i + 3 + row.ticker"
                type="container"
              >
                <mds-td colspan="5">
                  <mds-table spacious>
                    <mds-thead hidden-header>
                      <mds-th style="width: 40%;"> Fund Name </mds-th>
                      <mds-th style="width: 15%;"> Ticker </mds-th>
                      <mds-th> Fund Status </mds-th>
                      <mds-th> Pending </mds-th>
                      <mds-th style="width: 20%;"> Current Review Status </mds-th>
                    </mds-thead>
                    <mds-tbody>
                      <mds-tr
                        v-for="(clientDetail, idx) in row.FundAndReviewStatusesForClient"
                        :key="idx"
                      >
                        <mds-td class="client-row client-name">{{ clientDetail.client.clientName }}</mds-td>
                        <mds-td class="client-row"></mds-td>
                        <mds-td class="client-row">
                          <StatusIcons
                            :status="[{ name: clientDetail.currentStatus.name }]" :fullLength="true"
                          />
                        </mds-td>
                        <mds-td class="client-row">
                          <mds-icon v-if="clientDetail.isPending" class="pending-series-icon" name="clock-back"></mds-icon>
                        </mds-td>
                        <mds-td class="client-row">
                          <div v-if="clientDetail.reviewStatus">
                            <ReviewStatusIcons
                              :status="[clientDetail.reviewStatus]"
                              :fullLength="true"
                            />
                          </div>
                        </mds-td>
                      </mds-tr>
                    </mds-tbody>
                  </mds-table>
                </mds-td>
              </mds-tr>
            </template>
          </mds-tbody>
        </mds-table>
      </mds-section>
    </template>
  </internal-process>
</template>
<script>
import { has } from 'lodash';
import MdsSection from '@mds/section';
import {
  MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd,
} from '@mds/data-table';
import { goToRoute } from '@/utils';
import StatusIcons from '@/components/atoms/StatusIcons';
import ReviewStatusIcons from '@/components/atoms/ReviewStatusIcons';
import * as UniverseFundsService from '@/services/UniverseFunds';

export default {
  name: 'ReviewStatusAcrossClients',
  components: {
    MdsSection,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    StatusIcons,
    ReviewStatusIcons,
  },
  props: {
    fundId: {
      type: String,
      required: true,
    },
    fiduciaryFundId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      processTitle: 'Review Status Across Clients',
      withMainBackground: false,
      withFooterBackground: false,
      fundReviewDetails: null,
      rows: null,
      isLoading: false,
      filteredClientsReviewStatus: [],
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    backHandler() {
      goToRoute(this.$route, this.$router, '/');
    },
    fiterAllClientReviewStatus({ FundAndReviewStatusesForClient }) {
      const arrayOfClients = FundAndReviewStatusesForClient.map((client) => client.reviewStatus);
      return arrayOfClients.filter((item, pos) => item && arrayOfClients.indexOf(item) === pos);
    },
    toggleClient(idx) {
      if (!has(this.rows[idx], 'toggle')) {
        this.$set(this.rows[idx], 'toggle', false);
      } else if (this.rows[idx].toggle) {
        this.$set(this.rows[idx], 'toggle', false);
      } else {
        this.$set(this.rows[idx], 'toggle', true);
      }
    },
    checkIfClientExpanded(row) {
      if (!has(row, 'toggle')) {
        return true;
      }
      return row.toggle;
    },
    loadData() {
      this.isLoading = true;
      const queryParams = `&fundId=${this.fundId}&fiduciaryFundID=${this.fiduciaryFundId}`;
      UniverseFundsService.getReviewStatusAcrossClients(queryParams).then((result) => {
        this.isLoading = false;
        if (result.status.success) {
          this.fundReviewDetails = result.data;
          this.rows = result.data.sharedClasses;
        }
      }).catch((error) => {
        this.isLoading = false;
        this.handleError(error);
      });
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.$store.dispatch('openNotification', {
          message: error.message,
          notificationType: 'error',
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.review-status-across-clients-container {
  .fund-details-wrapper {
    padding-top: $mds-space-7-x;
    .fund-name-container {
      .field-value {
        @include mds-body-text-l($bold: true);
        margin-bottom: $mds-space-3-x;
        margin-top: 0;
      }
    }
    .fund-details-container {
      margin-bottom: $mds-space-3-x;
      display: flex;
      .fund-details {
        @include mds-body-text-m();
        &.fund-border {
          padding-right: $mds-space-1-and-a-half-x;
          margin-right: $mds-space-1-and-a-half-x;
          position:relative;
        }
        &.fund-border:after {
          content: "";
          position: absolute;
          top: 10%;
          right: 0;
          width: 1px;
          height: 80%;
          background: $mds-color-neutral-67;
        }
        .field-name {
          margin-right: $mds-space-three-quarter-x;
          @include mds-body-text-m($bold: true);
        }
      }
    }
  }
  .current-status-container {
    display: flex;
    padding: $mds-space-3-x 0;
    @include mds-body-text-l();
    .status-label {
      margin-right: $mds-space-1-x;
      @include mds-body-text-l($bold: true);
    }
    .status-value {
      display: flex;
      align-items: center;
      width: 200px;
    }
  }
  .review-status-share-class-container {
    .review-status-across-clients-table {
      td:not(.client-row) {
        border: none;
      }
      .share-class-fund-details:not(:first-child) {
        td {
          padding-top: $mds-space-5-x;
        }
      }
      .client-expandable {
        td {
          padding-top: $mds-space-2-x;
        }
      }
      .client-label {
        @include mds-body-text-s($bold: true);
      }
      .client-name {
        padding-left: $mds-space-2-and-a-half-x;
      }
      .client-row {
        background-color: #f2f2f2;
      }
    }
  }
}
</style>
<style lang="scss" module>
:global(.review-status-share-class-container) {
  &.mds-section {
    &.mds-section--expanded {
      &.mds-section--collapsible {
        &.mds-section--container {
          padding: $mds-space-2-x $mds-space-3-and-a-half-x $mds-space-3-and-a-half-x;
          .mds-icon {
            padding: 0;
          }
        }
      }
    }
  }
}
</style>
